import { useEffect, useState } from 'react';
import { choiceOf, nextChoice, themeLabel, themeOf, THEME_KEY, type Theme } from './theme';
import { IconMoon, IconSun } from './icons';

/**
 * Переключатель темы в шапке.
 *
 * ЗНАЧОК ПОКАЗЫВАЕТ ТО, ЧТО БУДЕТ, а не то, что есть. Луна при светлой
 * теме означает «нажми — станет тёмная»; солнце при тёмной — наоборот.
 * Обратное прочтение («значок показывает текущее состояние») выглядит
 * так же и работает так же — до первого человека, который прочтёт иначе;
 * поэтому рядом стоит подпись словами, и она снимает вопрос совсем.
 *
 * ТЕМА ЖИВЁТ НА КОРНЕ ДОКУМЕНТА, А НЕ В СОСТОЯНИИ ПРИЛОЖЕНИЯ. Окна
 * и подсказки рисуются порталом в конец страницы — вне дерева React, —
 * и переменные им достаются только сверху, от `<html>`.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ТА ЖЕ ТЕМА, НО БЕЗ КНОПКИ — ДЛЯ МЕНЮ ПРОФИЛЯ.
 *
 * Пакет 25.08 убрал переключатель темы из шапки кабинета: он уехал
 * пунктом в меню профиля, где рисуется совсем иначе — строкой
 * со значком слева, как «Пароль и доступ» рядом.
 *
 * Скопировать сюда три десятка строк переключения значило бы завести
 * ВТОРОЕ место, где решается, что писать в `localStorage` и когда
 * слушать систему. Разошлись бы они на первой же правке — и человек
 * получил бы кабинет, где тема из профиля не переживает перезагрузку,
 * а из админки переживает.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useTheme(): { dark: boolean; toggle(): void; label: string } {
  const [shown, setShown] = useState<Theme>(() => current());

  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => {
      if (choiceOf(read()) !== 'system') return;
      apply(themeOf('system', media.matches));
      setShown(current());
    };
    media.addEventListener('change', onSystem);
    return () => media.removeEventListener('change', onSystem);
  }, []);

  const toggle = () => {
    const choice = nextChoice(shown);
    try {
      localStorage.setItem(THEME_KEY, choice);
    } catch {
      // См. довод в `ThemeToggle`: запрещённое хранилище не мешает
      // переключать, оно мешает запомнить
    }
    apply(choice === 'dark' ? 'dark' : 'light');
    setShown(current());
  };

  return { dark: shown === 'dark', toggle, label: themeLabel(shown) };
}

export function ThemeToggle({ wide }: { wide?: boolean } = {}) {
  const [shown, setShown] = useState<Theme>(() => current());

  /**
   * СЛЕДИМ ЗА СИСТЕМОЙ, ПОКА ЧЕЛОВЕК НЕ ВЫБРАЛ САМ. Ноутбук переключается
   * на тёмную по расписанию; кабинет, оставшийся светлым до перезагрузки
   * страницы, выглядит забытым.
   */
  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => {
      if (choiceOf(read()) !== 'system') return;
      apply(themeOf('system', media.matches));
      setShown(current());
    };
    media.addEventListener('change', onSystem);
    return () => media.removeEventListener('change', onSystem);
  }, []);

  const flip = () => {
    const choice = nextChoice(shown);
    try {
      localStorage.setItem(THEME_KEY, choice);
    } catch {
      // Хранилище может быть запрещено. Тема от этого не перестаёт
      // переключаться — просто не переживёт перезагрузку.
    }
    apply(choice === 'dark' ? 'dark' : 'light');
    setShown(current());
  };

  const label = themeLabel(shown);

  // В ЯЩИКЕ РАЗДЕЛОВ — КНОПКОЙ СО СЛОВОМ, а не одним значком: там место
  // есть, и «Тёмная тема» снимает вопрос, который значок оставляет.
  // В шапке места нет, и там он остаётся значком.
  if (wide) {
    return (
      <button
        className="rk-button-quiet flex-1 justify-center"
        onClick={flip}
        title={label}
        style={{ height: 36 }}
      >
        {shown === 'dark' ? <IconSun /> : <IconMoon />}
        {label}
      </button>
    );
  }

  return (
    /**
     * `rk-head-icon` — не украшение, а изъятие из общего правила
     * касания: на телефоне все кнопки растут до 44, а значкам шапки
     * положено ровно 40×40 (ответ 34). Без этого имени переключатель
     * темы вышел бы на четыре точки выше соседнего колокольчика.
     */
    <button
      className="rk-icon-button rk-head-icon"
      onClick={flip}
      title={label}
      aria-label={label}
    >
      {shown === 'dark' ? <IconSun /> : <IconMoon />}
    </button>
  );
}

function read(): string | null {
  try {
    return localStorage.getItem(THEME_KEY);
  } catch {
    return null;
  }
}

function current(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function apply(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}
